/**
 * Una foto de producto, de la cámara a la tabla.
 *
 * Whatever the camera hands over — twelve megapixels, sideways, 4 MB — leaves here as a 320px
 * square of 15–35 KB named by the SHA-256 of its bytes. Everything happens on the device, so
 * taking a photo works with no signal: the picture shows on this phone immediately, and the
 * bytes go up the next time a sync finds the server (`sync/fotos.ts`).
 *
 * Square because every place a thumbnail is drawn is square, and a centre crop done once here
 * is better than a different crop on every screen. Downscaled in halvings rather than one step,
 * because a single 4000→320 reduction on a canvas skips most of the pixels and comes out
 * jagged — on exactly the fine print of a label that tells two products apart.
 */

import { FOTO_LADO, FOTO_MAX_BYTES, huellaFoto, tipoDeImagen, type TipoFoto } from '@abastece/shared';
import { db } from '../db/dexie.js';
import { emitir } from './emitir.js';

export interface FotoPreparada {
  huella: string;
  tipo: TipoFoto;
  datos: Blob;
}

/** The largest centred square inside a `ancho × alto` picture. */
export function recorteCuadrado(ancho: number, alto: number): { x: number; y: number; lado: number } {
  const lado = Math.min(ancho, alto);
  return { x: Math.floor((ancho - lado) / 2), y: Math.floor((alto - lado) / 2), lado };
}

/** The smallest square a `ancho × alto` picture fits in whole, and where it goes in it, centred. */
export function encajeEntero(ancho: number, alto: number): { x: number; y: number; lado: number } {
  const lado = Math.max(ancho, alto);
  return { x: Math.floor((lado - ancho) / 2), y: Math.floor((lado - alto) / 2), lado };
}

/**
 * The sizes a square passes through on its way down to `destino`: halvings while there is
 * more than a halving left, then the destination. Never up — a picture smaller than the
 * target is kept at the size it is, not blown up into mush.
 */
export function pasosDeReduccion(lado: number, destino: number = FOTO_LADO): number[] {
  if (lado <= destino) return [lado];
  const pasos: number[] = [];
  let actual = lado;
  while (actual > destino * 2) {
    actual = Math.round(actual / 2);
    pasos.push(actual);
  }
  pasos.push(destino);
  return pasos;
}

interface Decodificada {
  fuente: CanvasImageSource;
  ancho: number;
  alto: number;
  soltar: () => void;
}

/**
 * Decoded the right way up.
 *
 * `createImageBitmap` decodes off the main thread and, told to, honours the EXIF rotation a
 * phone camera writes instead of turning the pixels. It will not take an SVG, and a few
 * browsers refuse the options object, so an `<img>` is the fallback — which also honours EXIF,
 * because every current browser draws images `image-orientation: from-image` by default.
 */
async function decodificar(archivo: Blob): Promise<Decodificada> {
  if (typeof createImageBitmap === 'function' && archivo.type !== 'image/svg+xml') {
    try {
      const bmp = await createImageBitmap(archivo, { imageOrientation: 'from-image' });
      return { fuente: bmp, ancho: bmp.width, alto: bmp.height, soltar: () => bmp.close() };
    } catch {
      /* Fall through to the element, which decodes more than a bitmap will. */
    }
  }
  const url = URL.createObjectURL(archivo);
  try {
    const img = new Image();
    img.decoding = 'async';
    img.src = url;
    await img.decode();
    return { fuente: img, ancho: img.naturalWidth, alto: img.naturalHeight, soltar: () => {} };
  } finally {
    URL.revokeObjectURL(url);
  }
}

function lienzo(ancho: number, alto: number = ancho): [HTMLCanvasElement, CanvasRenderingContext2D] {
  const c = document.createElement('canvas');
  c.width = ancho;
  c.height = alto;
  const ctx = c.getContext('2d');
  if (!ctx) throw new Error('Canvas no disponible');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  return [c, ctx];
}

const aBlob = (c: HTMLCanvasElement, tipo: string, calidad: number) =>
  new Promise<Blob | null>((listo) => c.toBlob(listo, tipo, calidad));

/**
 * WebP where the browser can write it, JPEG where it cannot.
 *
 * Safari decodes WebP but will not encode it: asked for one, `toBlob` quietly hands back a PNG
 * several times the size. So the answer is checked rather than trusted, and the quality steps
 * down in the rare case a busy picture comes out over the server's limit.
 */
async function codificar(c: HTMLCanvasElement): Promise<Blob> {
  for (const calidad of [0.82, 0.7, 0.55]) {
    const webp = await aBlob(c, 'image/webp', calidad);
    const blob = webp?.type === 'image/webp' ? webp : await aBlob(c, 'image/jpeg', calidad + 0.03);
    if (blob && blob.size <= FOTO_MAX_BYTES) return blob;
  }
  throw new Error('La foto no se pudo reducir lo suficiente');
}

/**
 * Any picture in, a named square out. Writes nothing.
 *
 * `entera`: the whole picture in the square, on white, rather than the square out of its middle. A
 * photo taken here is framed on the product and loses only its edges; a picture from the internet
 * is often a tall bottle or a wide box on white, and its middle alone would cut off a cap or a name.
 */
export async function prepararFoto(archivo: Blob, { entera = false }: { entera?: boolean } = {}): Promise<FotoPreparada> {
  const img = await decodificar(archivo);
  try {
    if (!img.ancho || !img.alto) throw new Error('La imagen está vacía');
    const { x, y, lado } = entera ? encajeEntero(img.ancho, img.alto) : recorteCuadrado(img.ancho, img.alto);

    const [cuadrado, ctx] = lienzo(lado);
    // A background first, so a transparent PNG does not come out black as a JPEG.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, lado, lado);
    if (entera) ctx.drawImage(img.fuente, x, y, img.ancho, img.alto);
    else ctx.drawImage(img.fuente, x, y, lado, lado, 0, 0, lado, lado);

    let actual = cuadrado;
    for (const paso of pasosDeReduccion(lado)) {
      if (paso === actual.width) continue;
      const [siguiente, ctxSiguiente] = lienzo(paso);
      ctxSiguiente.drawImage(actual, 0, 0, paso, paso);
      actual = siguiente;
    }

    const datos = await codificar(actual);
    const bytes = new Uint8Array(await datos.arrayBuffer());
    const tipo = tipoDeImagen(bytes);
    if (!tipo) throw new Error('El navegador produjo una imagen que no se reconoce');
    return { huella: await huellaFoto(bytes), tipo, datos };
  } finally {
    img.soltar();
  }
}

// ---------------------------------------------------------------------------
// Documentos
// ---------------------------------------------------------------------------

/** Longest side of a photo of a document, in pixels: enough to read a cédula's name and number. */
export const DOCUMENTO_LADO = 1280;

/**
 * The sizes a `ancho × alto` picture passes through on its way to a longest side of `lado`:
 * halvings of the longest side, like a product photo, with the other side in proportion. Never
 * up — a picture already smaller stays the size it is.
 */
export function pasosDeDocumento(
  ancho: number,
  alto: number,
  lado: number = DOCUMENTO_LADO,
): { ancho: number; alto: number }[] {
  const largo = Math.max(ancho, alto);
  return pasosDeReduccion(largo, Math.min(lado, largo)).map((l) => ({
    ancho: Math.max(1, Math.round((ancho * l) / largo)),
    alto: Math.max(1, Math.round((alto * l) / largo)),
  }));
}

/**
 * A photo of a document, for reading rather than for recognising: the whole picture instead
 * of a square out of its middle, and big enough that the name and the number on a cédula can
 * be read back. A picture too busy to fit the server's limit at full size tries a smaller one.
 */
export async function prepararDocumento(archivo: Blob): Promise<FotoPreparada> {
  const img = await decodificar(archivo);
  try {
    if (!img.ancho || !img.alto) throw new Error('La imagen está vacía');
    let ultimo: unknown;
    for (const lado of [DOCUMENTO_LADO, 1024, 800]) {
      // Straight from the source into the first step, which is at most a halving of it: a
      // full-size canvas of a twelve-megapixel photo is forty-eight megabytes for nothing.
      let actual: HTMLCanvasElement | null = null;
      for (const paso of pasosDeDocumento(img.ancho, img.alto, lado)) {
        const [siguiente, ctx] = lienzo(paso.ancho, paso.alto);
        if (actual === null) {
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, paso.ancho, paso.alto);
          ctx.drawImage(img.fuente, 0, 0, paso.ancho, paso.alto);
        } else {
          ctx.drawImage(actual, 0, 0, paso.ancho, paso.alto);
        }
        actual = siguiente;
      }
      try {
        const datos = await codificar(actual!);
        const bytes = new Uint8Array(await datos.arrayBuffer());
        const tipo = tipoDeImagen(bytes);
        if (!tipo) throw new Error('El navegador produjo una imagen que no se reconoce');
        return { huella: await huellaFoto(bytes), tipo, datos };
      } catch (e) {
        ultimo = e;
      }
    }
    throw ultimo;
  } finally {
    img.soltar();
  }
}

/** A photo of a document in, the name of a picture this device now holds out. */
export async function documentoDeArchivo(archivo: Blob): Promise<string> {
  return guardarFoto(await prepararDocumento(archivo));
}

/**
 * Keeps the bytes on this device, waiting for the server.
 *
 * Content-addressed, so taking the same photo twice is one row — and a photo this device
 * already fetched from the server is not demoted back to "waiting to upload".
 */
export async function guardarFoto(f: FotoPreparada): Promise<string> {
  await db.transaction('rw', db.fotos, async () => {
    if (await db.fotos.get(f.huella)) return;
    await db.fotos.put({ ...f, estado: 'pendiente', creadaEn: new Date().toISOString() });
  });
  return f.huella;
}

/** Camera or gallery file in, the name of a photo this device now holds out; `entera` as `prepararFoto`. */
export async function fotoDeArchivo(archivo: Blob, opciones?: { entera?: boolean }): Promise<string> {
  return guardarFoto(await prepararFoto(archivo, opciones));
}

/** One event, and the photo is the product's on every device that syncs. */
export async function ponerFoto(productoId: string, huella: string): Promise<void> {
  await emitir('producto.actualizado', { productoId, foto: huella });
}

/**
 * Takes the picture off the product. The bytes stay where they are — on this device and on
 * the server — because a photo is shared by name and another product may be wearing it.
 */
export async function quitarFoto(productoId: string): Promise<void> {
  await emitir('producto.actualizado', { productoId, foto: null });
}
